"use client";

import { useState } from "react";
import { Stack, Typography, InputBase, TextareaAutosize, Checkbox } from "@mui/material";
import Image from "next/image";
import theme from "@/theme/theme";
import SideTabbedButton from "@/components/generic/SideTabbedButton";

import arrow from "@/assets/images/icons/arrow.webp";

type SubmitStatus = "idle" | "loading" | "success" | "error";

const inputSx = {
    width: "100%",
    border: "1px solid #2F2F2F",
    borderRadius: 2,
    padding: 1,
    background: "transparent",
    color: theme.palette.text.primary,
    "&::placeholder": {
        color: theme.palette.text.secondary,
    },
    "&:hover": {
        borderColor: "#FFFFFF",
    }
};

const QrScanCta = () => {
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [message, setMessage] = useState("");
    const [agreeToProcessing, setAgreeToProcessing] = useState(false);
    const [status, setStatus] = useState<SubmitStatus>("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const handleSubmit = async () => {
        if (status === "loading") return;

        if (!fullName.trim()) {
            setStatus("error");
            setErrorMsg("Please enter your full name.");
            return;
        }

        if (!email.trim()) {
            setStatus("error");
            setErrorMsg("Please enter your work email.");
            return;
        }

        if (!agreeToProcessing) {
            setStatus("error");
            setErrorMsg("Please agree to the processing of personal data to continue.");
            return;
        }

        setStatus("loading");
        setErrorMsg("");

        try {
            const res = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    fullName: fullName.trim(),
                    email: email.trim(),
                    phone: phone.trim() || null,
                    entityTypes: null,
                    inquiryOptions: { "ZK & PQC": ["Post-Quantum Migration", "Free PQC Scan"] },
                    message: `[PQC Free Scan] ${message.trim()}`.trim(),
                    agreeToProcessing: true,
                }),
            });

            const data = (await res.json()) as { ok: boolean; error?: string };

            if (!res.ok || !data.ok) {
                throw new Error(
                    data.error ?? "We couldn't send your request. Please try again, or email us directly.",
                );
            }

            setStatus("success");
            setFullName("");
            setEmail("");
            setPhone("");
            setMessage("");
        } catch (e) {
            setStatus("error");
            setErrorMsg(
                e instanceof Error
                    ? e.message
                    : "We couldn't send your request. Please try again, or email us directly.",
            );
        }
    };

    return (
        <Stack
            width={{ xs: "100%", lg: "80%" }}
            marginX="auto"
            paddingX={4}
            gap={6}
            position="relative"
            direction={{ xs: "column", lg: "row" }}
            alignItems="start"
        >
            <Stack width={{ xs: "100%", lg: "50%" }} gap={2}>
                <Typography variant="h3" component="h3" className="chno" fontWeight={500} textTransform="uppercase" fontSize={{ xs: "1.5rem !important", lg: "2.25rem !important" }}>
                    Request a free scan
                </Typography>
                <Typography variant="body2" component="p" fontSize="0.95rem !important" color={theme.palette.text.secondary} fontWeight={400}>
                    See where your organisation stands. We run our framework against a repository of your
                    choice, inside your own infrastructure, read-only, with no data leaving your perimeter.
                    Afterwards we walk you through the crypto inventory and findings together.
                </Typography>
                <Typography variant="body2" component="p" fontSize="0.95rem !important" color={theme.palette.text.secondary} fontWeight={400}>
                    The scan is free and works like an initial audit: no commitment and no preparation needed.
                    Leave your details and we&apos;ll get in touch to set it up.
                </Typography>
            </Stack>
            <Stack width={{ xs: "100%", lg: "50%" }} gap={2}>
                <Typography component="p" variant="body2" fontSize="0.8rem !important" color={theme.palette.text.secondary}>
                    Fields marked * are required.
                </Typography>
                <InputBase
                    placeholder="Full Name *"
                    inputProps={{ 'aria-label': 'Full Name (required)' }}
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    disabled={status === "loading"}
                    sx={inputSx}
                />
                <InputBase
                    placeholder="Work Email *"
                    inputProps={{ 'aria-label': 'Work Email (required)' }}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={status === "loading"}
                    sx={inputSx}
                />
                <InputBase
                    placeholder="Phone Number (optional)"
                    inputProps={{ 'aria-label': 'Phone Number' }}
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={status === "loading"}
                    sx={inputSx}
                />
                <TextareaAutosize
                    placeholder="Organisation, repository host (GitHub, GitLab, ...), anything we should know"
                    aria-label="Your message"
                    minRows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={status === "loading"}
                    style={{
                        width: "100%",
                        border: "1px solid #2F2F2F",
                        borderRadius: "0.6rem",
                        padding: "0.6rem",
                        background: "transparent",
                        color: theme.palette.text.primary,
                    }}
                />
                <Stack direction="row" alignItems="center" gap={1}>
                    <Checkbox
                        edge="start"
                        slotProps={{ input: { 'aria-label': 'Agree to personal data processing' } }}
                        checked={agreeToProcessing}
                        onChange={(e) => setAgreeToProcessing(e.target.checked)}
                        disabled={status === "loading"}
                        disableRipple
                        sx={{
                            paddingRight: 0,
                            color: theme.palette.text.primary,
                            "&.Mui-checked": { color: theme.palette.text.primary },
                        }}
                    />
                    <Typography component="span" variant="body2" fontWeight={300} color={theme.palette.text.secondary}>
                        I agree to the processing of personal data
                    </Typography>
                </Stack>
                <Stack width={{ xs: "100%", lg: "fit-content" }}>
                    <SideTabbedButton fullWidth paddingX={18} hoverShiftX={1.5} hoverShiftY={-1} action={handleSubmit} disabled={status === "loading"}>
                        <div className="flex items-center justify-center gap-1">
                            <Typography component="span" variant="h6" marginRight={0.5}>
                                {status === "loading" ? "Sending..." : "Request scan"}
                            </Typography>
                            <span className="arrow-clip">
                                <span className="arrow-slide">
                                    <Image src={arrow} alt="arrow animation" width={20} height={20} style={{ display: "block" }} />
                                </span>
                            </span>
                        </div>
                    </SideTabbedButton>
                </Stack>
                {status === "error" && (
                    <Typography variant="body2" role="alert" color="error">
                        {errorMsg}
                    </Typography>
                )}
                {status === "success" && (
                    <Typography variant="body2" role="alert" color="success.main">
                        Thanks — we&apos;ll be in touch to schedule your free scan.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
};

export default QrScanCta;
